import React, { useRef } from 'react';
import { motion, useScroll } from 'framer-motion';
import { Download, Share2, Key, LogOut } from 'lucide-react';
import { useThemeMode } from '../../hooks/useThemeMode';

const steps = [
  {
    icon: Download,
    title: "Deposit Any Token",
    desc: "Send SOL, ETH, HYPE, USDC, or any token to our treasury on Solana or EVM chains. Instant confirmation.",
    tag: "Input"
  },
  {
    icon: Share2,
    title: "Choose Options",
    desc: "Withdraw on same chain or bridge between ecosystems. Multi-chain splits supported for obfuscation.",
    tag: "Configure"
  },
  {
    icon: Key,
    title: "Receive OTS Keys",
    desc: "Get One-Time-Signature keys (Ed25519 for Solana, ECDSA for EVM) for your target chains.",
    tag: "Secure"
  },
  {
    icon: LogOut,
    title: "Withdraw Privately",
    desc: "Use keys to withdraw with complete privacy to a fresh wallet. Flexible timing prevents time-analysis.",
    tag: "Output"
  }
];

const HowItWorks = () => {
  const containerRef = useRef(null);
  const { isDark } = useThemeMode();
  useScroll({
    target: containerRef,
    offset: ["start center", "end center"]
  });

  return (
    <section id="how-it-works" ref={containerRef} className="py-24 px-6 max-w-7xl mx-auto relative">
      <div className="mb-16">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className={`text-3xl md:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-neutral-900'}`}
        >
          Protocol Logic
        </motion.h2>
        <motion.div
          initial={{ opacity: 0, width: 0 }}
          whileInView={{ opacity: 1, width: 64 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="h-1"
          style={{ backgroundColor: isDark ? '#ffffff' : '#171717' }}
        />
      </div>

      <div className="relative">
        {/* Animated Connecting Path (Desktop) */}
        <div className="hidden lg:block absolute top-[4.5rem] left-0 w-full h-1 z-0">
          <svg className="w-full h-full overflow-visible">
            <motion.path
              d="M 50 0 L 1200 0"
              fill="none"
              stroke={isDark ? '#262626' : '#e5e5e5'}
              strokeWidth="1"
              strokeDasharray="8 8"
            />
            <motion.path
              d="M 50 0 L 1200 0"
              fill="none"
              stroke={isDark ? '#ffffff' : '#171717'}
              strokeWidth="1"
              strokeDasharray="8 8"
              initial={{ pathLength: 0 }}
              whileInView={{ pathLength: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.5, ease: "easeInOut" }}
            />
          </svg>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 z-10 relative">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.2, duration: 0.6 }}
              whileHover={{ y: -5, boxShadow: isDark ? "0 20px 40px -20px rgba(0,0,0,0.5)" : "0 20px 40px -20px rgba(0,0,0,0.1)" }}
              className={`group relative p-6 border transition-all duration-300 ${isDark ? 'border-neutral-800 hover:border-neutral-500' : 'border-neutral-200 hover:border-neutral-400'}`}
              style={{ backgroundColor: isDark ? '#0a0a0a' : '#ffffff' }}
            >
              {/* Step Number Background */}
              <div className="absolute top-4 right-4 text-6xl font-bold select-none transition-colors" style={{ color: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)' }}>
                0{index + 1}
              </div>

              <div
                className={`mb-6 inline-flex p-3 rounded-sm transition-colors duration-300 relative z-10 ${
                  isDark
                    ? 'text-white group-hover:bg-white group-hover:text-black'
                    : 'text-white group-hover:bg-neutral-900 group-hover:text-white'
                }`}
                style={{ backgroundColor: '#171717' }}
              >
                <step.icon size={24} />
              </div>

              <div className="mb-2 text-xs font-mono text-neutral-500 uppercase tracking-widest">
                Step {index + 1} &bull; {step.tag}
              </div>

              <h3 className={`text-xl font-bold mb-3 ${isDark ? 'text-white' : 'text-neutral-900'}`}>{step.title}</h3>
              <p className={`text-sm leading-relaxed transition-colors ${isDark ? 'text-neutral-400 group-hover:text-neutral-300' : 'text-neutral-500 group-hover:text-neutral-600'}`}>
                {step.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
